// pkg72 — LawMighty retheme: brand hex sweep (#1a1a1a→#0F1838 NAVY_DEEP, #0f0f0f→#0A1028, #f8f8f8→#F5F1E8 CREAM, #c0392b→#B85042 TERRA) — delivered 2026-07-12
// pkg32 — Emergency Alert banner.
//
// Rendered at the top of BOTH portal shells (client Layout + AdminLayout) so a
// firm's emergency notice shows on every page for clients and staff alike. The
// alert data rides on the public firm-info payload already loaded once at app
// startup (FirmSettingsContext), so there's no extra fetch here.
//
// Non-dismissible by design: while the alert is on it stays on every page. The
// admin "Emergency Alert" toggle is the single off switch.
//
// Built to render a LIST of alerts. Today that's just the firm's own banner.
// When the platform-wide broadcast ships (set by the platform owner, shown on
// every firm), the public payload will carry `firm.platformAlert` and it drops
// in on top with no change to the layouts — see the seam in AlertBanner below.

import React from 'react';
import { useFirmSettings } from '../contexts/FirmSettingsContext';

// Color palette per severity level. Mirrors the .alert-* palette in index.css
// (info = neutral grey, critical = the error red), with an amber middle tier
// for "warning". Exported so the admin control page can show an exact live
// preview without re-deriving these colors.
export const ALERT_LEVEL_STYLES = {
  info:     { bg: '#f5f5f5', border: '#dddddd', text: '#555555' },
  warning:  { bg: '#FAEEDA', border: '#EF9F27', text: '#633806' },
  critical: { bg: '#fdf0ef', border: '#f5c6c3', text: '#B85042' },
};

export function alertLevelStyle(level) {
  return ALERT_LEVEL_STYLES[level] || ALERT_LEVEL_STYLES.warning;
}

function AlertIcon({ level, color }) {
  const common = { width: 18, height: 18, viewBox: '0 0 24 24', style: { flexShrink: 0, marginTop: 1 }, 'aria-hidden': true };
  if (level === 'info') {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="10" fill="none" stroke={color} strokeWidth="2" />
        <line x1="12" y1="11" x2="12" y2="16" stroke={color} strokeWidth="2" strokeLinecap="round" />
        <circle cx="12" cy="8" r="1.3" fill={color} />
      </svg>
    );
  }
  // warning + critical share a triangle "!"
  return (
    <svg {...common}>
      <path d="M12 3 L22 20 H2 Z" fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" />
      <line x1="12" y1="9" x2="12" y2="14" stroke={color} strokeWidth="2" strokeLinecap="round" />
      <circle cx="12" cy="17" r="1.3" fill={color} />
    </svg>
  );
}

// A single alert row. Exported so the admin page can render a live preview
// identical to what clients see.
export function AlertBannerRow({ level, message }) {
  const s = alertLevelStyle(level);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: '12px 16px',
        background: s.bg,
        borderBottom: `1px solid ${s.border}`,
        fontFamily: 'Arial, sans-serif',
        fontSize: 13,
        lineHeight: 1.5,
        color: s.text,
      }}
    >
      <AlertIcon level={level} color={s.text} />
      <div style={{ whiteSpace: 'pre-wrap' }}>{message}</div>
    </div>
  );
}

export default function AlertBanner() {
  const { firm } = useFirmSettings();

  const alerts = [];
  // Seam for the future platform-wide broadcast. When it ships, firm-info will
  // include firm.platformAlert; pushing it first keeps it on top of the stack.
  if (firm && firm.platformAlert && firm.platformAlert.message) {
    alerts.push({ key: 'platform', level: firm.platformAlert.level, message: firm.platformAlert.message });
  }
  if (firm && firm.alert && firm.alert.message) {
    alerts.push({ key: 'firm', level: firm.alert.level, message: firm.alert.message });
  }

  if (!alerts.length) return null;

  return (
    <div role="alert" aria-live="polite">
      {alerts.map(a => (
        <AlertBannerRow key={a.key} level={a.level} message={a.message} />
      ))}
    </div>
  );
}
